// 右側小紅帽角色立繪 — Unity 來源 Spine 骨骼動畫 ani_character。
// 主遊戲：待機 small_idle(loop)、閒置隨機 small_cute（characterIdle 事件）、中獎 small_happy。
// 免費遊戲（依 Scatter 計量等級 1~4）：進場 small_upgrade→big1_show→big1_idle（由小變大），
//   中獎 big{n}_win（音效 pistol/bazooka 由 AudioManager 播）、升級 big{n-1}_upgrade→big{n}_idle。
// 對照 Unity GamePlayUI 的 PlayCharacterWin/PlayCharacterUpgrade/_bigCharacterIdleAnimations。
// 骨骼載入失敗退回靜態立繪 bg_loadingCharacter.png。

import { Container, Sprite } from 'pixi.js'
import type { Spine } from '@esotericsoftware/spine-pixi-v8'
import type { Orientation, Resizable } from '../../../framework/render/design'
import {
  layoutMetrics,
  unityAnchorLandscape,
  unityAnchorPortrait,
  unityScaleLandscape,
  unityScalePortrait,
} from '../../../framework/render/design'
import { delay } from '../../../framework/core/async'
import { killTweensOf, tweenTo } from '../tween'
import { getUiTexture } from '../../assets/UiTextures'
import { CHARACTER_ANIM, createCharacterSpine } from '../../assets/CharacterSpine'
import type { EventBus } from '../../../framework/events/EventBus'
import type { GameEventMap } from '../../events/GameEvents'

/**
 * Unity LandscapeUI.prefab 的 Character 節點：aPos (838, −224.4)、scale 1，主遊戲/免費遊戲同一位置縮放
 * （只切換動畫，小/大形態尺寸由骨骼動畫本身決定）。骨骼原點即該節點位置。
 */
const CHARACTER_AX = 838
const CHARACTER_AY = -224.4
/** 直版覆寫（PoriraitUI Character aPos (383.7, 339.6)、scale 1.2）。 */
const CHARACTER_P_AX = 383.7
const CHARACTER_P_AY = 339.6
const CHARACTER_P_SCALE = 1.2

export class CharacterView extends Container implements Resizable {
  private readonly fallbackSprite = new Sprite(getUiTexture('character'))
  private readonly spine: Spine | null
  /** 免費遊戲模式（true 時用 big{level} 系列動畫）。 */
  private freeGame = false
  /** FG 計量等級（1~4），決定 big{n} 待機/中獎動畫。 */
  private fgLevel = 1
  /** 是否正在播升級/進場等「不可被中獎打斷」的演出。 */
  private busy = false

  constructor(private readonly bus: EventBus<GameEventMap>) {
    super()
    // 靜態立繪 fallback：底部中心錨，方便腳部對齊底部操作列上緣。
    this.fallbackSprite.anchor.set(0.5, 1)
    this.addChild(this.fallbackSprite)

    this.spine = createCharacterSpine()
    if (this.spine !== null) {
      this.fallbackSprite.visible = false
      this.addChild(this.spine)
      this.playIdle()
    }

    // 中獎演出（每個消除步驟開始時）；升級/進場演出中不打斷。
    this.bus.on('stepWinPresented', ({ freeGame, fgLevel }) => {
      if (this.busy) return
      this.playWin(freeGame, fgLevel)
    })
    this.bus.on('spinStateChanged', ({ spinning }) => {
      if (!spinning && !this.busy) this.playIdle()
    })
    // 閒置隨機小動作（GameController 於 idle 狀態每 30~31s 觸發，對照 Unity StartCharacterIdle）。
    this.bus.on('characterIdle', () => {
      if (this.busy || this.freeGame || this.spine === null) return
      this.playOnce(CHARACTER_ANIM.cute, this.idleAnimation())
    })
  }

  /** 目前模式的待機動畫名。 */
  private idleAnimation(): string {
    if (!this.freeGame) return CHARACTER_ANIM.idle
    const index = Math.max(1, Math.min(CHARACTER_ANIM.fgIdle.length, this.fgLevel)) - 1
    return CHARACTER_ANIM.fgIdle[index]!
  }

  /** 播一次 name 後自動接 followIdle loop（動畫不存在時安全略過）。 */
  private playOnce(name: string, followIdle: string): number {
    if (this.spine === null) return 0
    const animation = this.spine.skeleton.data.findAnimation(name)
    if (animation === null) return 0
    this.spine.state.setAnimation(0, name, false)
    if (this.spine.skeleton.data.findAnimation(followIdle) !== null) {
      this.spine.state.addAnimation(0, followIdle, true, 0)
    }
    return animation.duration * 1000
  }

  /**
   * 進入免費遊戲：small_upgrade（變身）→ big1_show（登場）→ big1_idle。
   * 對照 Unity GamePlayUI.ToFg 的 PlayCharacterUpgrade(0)；升級音效/語音由 AudioManager 於 scatterLevelUp 播。
   */
  async enterFreeGame(): Promise<void> {
    this.freeGame = true
    this.fgLevel = 1
    if (this.spine === null) {
      await this.fallbackPulse()
      return
    }
    this.busy = true
    const upgrade = this.spine.skeleton.data.findAnimation(CHARACTER_ANIM.upgrade)
    const show = this.spine.skeleton.data.findAnimation(CHARACTER_ANIM.bigShow)
    let totalMs = 0
    if (upgrade !== null) {
      this.spine.state.setAnimation(0, CHARACTER_ANIM.upgrade, false)
      totalMs += upgrade.duration * 1000
      if (show !== null) {
        this.spine.state.addAnimation(0, CHARACTER_ANIM.bigShow, false, 0)
        totalMs += show.duration * 1000
      }
      this.spine.state.addAnimation(0, this.idleAnimation(), true, 0)
    } else {
      totalMs = this.playOnce(CHARACTER_ANIM.bigShow, this.idleAnimation())
    }
    await delay(Math.max(400, totalMs))
    this.busy = false
  }

  /** 離開免費遊戲：回主遊戲小紅帽待機。 */
  exitFreeGame(): void {
    this.freeGame = false
    this.fgLevel = 1
    this.busy = false
    this.playIdle()
  }

  /**
   * Scatter 計量升級演出：播 big{level-1}_upgrade（升到 level）再回 big{level}_idle，供 GameController await。
   * 對照 Unity GamePlayUI.PlayCharacterUpgrade（_characterUpgradeAnimations[checkLevel-1]）。
   */
  async playUpgrade(level: number): Promise<void> {
    this.freeGame = true
    this.fgLevel = Math.max(1, Math.min(4, Math.floor(level)))
    this.busy = true
    if (this.spine !== null) {
      const index = Math.max(2, Math.min(CHARACTER_ANIM.fgUpgrade.length + 1, this.fgLevel)) - 2
      const ms = this.playOnce(CHARACTER_ANIM.fgUpgrade[index]!, this.idleAnimation())
      await delay(Math.max(400, ms))
    } else {
      await this.fallbackPulse()
    }
    this.busy = false
  }

  /** 無骨骼 fallback：對立繪做一次縮放脈衝當作演出強調。 */
  private async fallbackPulse(): Promise<void> {
    const base = this.fallbackSprite.scale.x
    killTweensOf(this.fallbackSprite.scale)
    this.fallbackSprite.scale.set(base * 1.18)
    await tweenTo(this.fallbackSprite.scale, { x: base, y: base, duration: 0.5, ease: 'back.out(2)' })
  }

  private playIdle(): void {
    if (this.spine === null) return
    const idle = this.idleAnimation()
    if (this.spine.skeleton.data.findAnimation(idle) === null) return
    this.spine.state.setAnimation(0, idle, true)
  }

  /** 中獎演出：主遊戲 small_happy；FG 依等級 big{n}_win（播完自動回待機）。 */
  private playWin(freeGame: boolean, fgLevel: number): void {
    if (this.spine === null) return
    if (freeGame) {
      this.freeGame = true
      this.fgLevel = Math.max(1, Math.min(4, Math.floor(fgLevel)))
      const index = this.fgLevel - 1
      this.playOnce(CHARACTER_ANIM.fgWin[index]!, this.idleAnimation())
    } else {
      this.playOnce(CHARACTER_ANIM.win, this.idleAnimation())
    }
  }

  resize(width: number, height: number, orientation: Orientation): void {
    const barHeight = height * layoutMetrics(orientation).barHeight
    const isPortrait = orientation === 'portrait'

    if (this.spine !== null && !isPortrait) {
      // 橫版照 Unity Character 節點：骨骼原點放 aPos (838, −224.4)、等比縮放係數即 scale 1 的換算。
      // 不做 bounds 正規化——主遊戲小紅帽與 FG 大形態的尺寸差異由骨骼動畫本身呈現（Unity 行為）。
      const s = unityScaleLandscape(width, height)
      const origin = unityAnchorLandscape(CHARACTER_AX, CHARACTER_AY, width, height)
      this.spine.scale.set(s)
      this.spine.position.set(origin.x, origin.y)
      return
    }
    if (this.spine !== null && isPortrait) {
      // 直版照 PoriraitUI 覆寫：Character aPos (383.7, 339.6)、scale ×1.2。
      const s = unityScalePortrait(width, height) * CHARACTER_P_SCALE
      const origin = unityAnchorPortrait(CHARACTER_P_AX, CHARACTER_P_AY, width, height)
      this.spine.scale.set(s)
      this.spine.position.set(origin.x, origin.y)
      return
    }

    // 靜態立繪 fallback：維持 bounds 正規化的比例佈局。
    const targetHeight = height * (isPortrait ? 0.46 : 0.8)
    const cx = isPortrait ? width * 0.8 : width * 0.9
    const footY = height - barHeight * 0.55

    if (this.spine !== null) {
      this.spine.scale.set(1)
      const bounds = this.spine.getLocalBounds()
      const bh = bounds.height
      const scale = bh > 1 ? targetHeight / bh : targetHeight / 685
      this.spine.scale.set(scale)
      // 以骨骼邊界的「底部中心」對齊 (cx, footY)：x 置中、y 對齊腳部。
      const centerX = bounds.x + bounds.width / 2
      const bottomY = bounds.y + bounds.height
      this.spine.position.set(cx - centerX * scale, footY - bottomY * scale)
    } else {
      const scale = targetHeight / this.fallbackSprite.texture.height
      this.fallbackSprite.scale.set(scale)
      this.fallbackSprite.position.set(cx, footY)
    }
  }
}
